import { ErrorMessage } from '@hookform/error-message'
import { zodResolver } from '@hookform/resolvers/zod'
import { Error } from '@progress/kendo-react-labels'
import { type PrimitiveAtom } from 'jotai'
import React from 'react'
import { type SubmitHandler, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAsyncFn } from 'react-use'
import { type Asyncify, type Promisable } from 'type-fest'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledInput } from '~/components/elements/StyledInput'
import { useTurnOffAtom } from '~/hooks/jotai'
import { useFormField } from '~/hooks/useFormField'

import { type InputValues, type SavedFilter } from '../utils'

const saveDialogSchema = z.object({ title: z.string().min(1) })
type SaveDialogInputValues = z.infer<typeof saveDialogSchema>

export type SaveFilterDialogProps<TFilterItem> = {
  filters: SavedFilter[]
  isVisibleSaveDialogAtom: PrimitiveAtom<boolean>
  onSave?: (
    title: string,
    filterGroups: InputValues<TFilterItem>['filterGroups'],
  ) => Promisable<void>
}

export const SaveFilterDialog = <TFilterItem,>({
  filters,
  isVisibleSaveDialogAtom,
  onSave,
}: SaveFilterDialogProps<TFilterItem>) => {
  const { t } = useTranslation()

  const resolver = React.useMemo(
    () =>
      zodResolver(
        saveDialogSchema.refine(
          ({ title }) => filters.every((filter) => filter.title !== title),
          {
            path: ['title'],
            message: t('a_search_condition_with_the_same_name_already_exists'),
          },
        ),
      ),
    [filters, t],
  )

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<SaveDialogInputValues>({
    defaultValues: { title: '' },
    mode: 'onBlur',
    resolver,
  })
  const titleField = useFormField(control, 'title')

  const { getValues } = useFormContext<InputValues<TFilterItem>>()
  const closeSaveDialog = useTurnOffAtom(isVisibleSaveDialogAtom)
  const [handleValidState, handleValid] = useAsyncFn<
    Asyncify<SubmitHandler<SaveDialogInputValues>>
  >(
    async ({ title }) => {
      const filterGroups = getValues('filterGroups')

      await onSave?.(title, filterGroups)

      closeSaveDialog()
    },
    [closeSaveDialog, getValues, onSave],
  )

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={handleValidState.loading}
      mode="create"
      title={t('save_search_condition')}
      onCancel={closeSaveDialog}
      onExecute={handleSubmit(handleValid)}
    >
      <LabelingContainer label={t('search_condition_name')}>
        <StyledInput autoFocus={true} width="100%" {...titleField} />

        <ErrorMessage
          errors={errors}
          name="title"
          render={({ message }) => <Error>{message}</Error>}
        />
      </LabelingContainer>
    </ExecuteCancelDialog>
  )
}
